import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, CircleDot, Lock } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const GasketsSeals = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["services/gaskets-seals"]?.title} description={seoData["services/gaskets-seals"]?.description} path="/services/gaskets-seals" keywords={seoData["services/gaskets-seals"]?.keywords || []} />

      <PageHero
        badge="Machinery & Equipment"
        title="Gaskets, Seals & Flange Repair"
        description="Custom gasket and seal solutions for industrial equipment. Emergency gasket fabrication, flange face repair, and sealing solutions using Belzona elastomeric and metal repair systems. Eliminate recurring leak problems."
        backgroundKey="gaskets-seals"
        buttonText="Get In Touch"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Problems We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Recurring flange leaks from corroded gasket surfaces',
              'Emergency gasket needs when OEM parts are unavailable',
              'Worn or damaged flange faces preventing proper seal',
              'Manway and handhole cover sealing problems',
              'Heat exchanger channel head gasket failures',
              'Split-line and casing joint leaks'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-rose-50 rounded-lg">
                <CircleDot className="text-rose-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-rose-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Lock className="text-rose-600 mr-3 flex-shrink-0" size={22} />
                  Flange Face Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Rebuild corroded, scored, and damaged flange faces to proper flatness and finish using Belzona metal repair composites. Restore sealing surfaces without machining or replacement.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-rose-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Lock className="text-rose-600 mr-3 flex-shrink-0" size={22} />
                  Emergency Gasket Fabrication
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">On-site gasket creation using Belzona liquid elastomers. Form gaskets directly on equipment — no measurements, no templates, perfect fit every time. Ideal when OEM gaskets are unavailable or lead times are unacceptable.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-rose-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Lock className="text-rose-600 mr-3 flex-shrink-0" size={22} />
                  Manway & Access Cover Sealing
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Repair and reseal manway covers, handhole plates, and access doors using conformable gasket and sealing systems. Eliminate recurring leaks from distorted or corroded covers.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-rose-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Lock className="text-rose-600 mr-3 flex-shrink-0" size={22} />
                  Permanent Leak Elimination
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Identify and permanently fix the root cause of recurring seal and gasket failures. Flange face rebuild, alignment correction, and proper gasket system selection to eliminate repeat failures.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS</h2>
              <div className="space-y-3">
                {['Emergency gasket fabrication on-site — no lead time',
                  'Perfect-fit gaskets without measurements or templates',
                  'Permanent flange face repair eliminates recurring leaks',
                  'No hot work or machining required',
                  'Works on all flange types, sizes, and pressure classes',
                  'Chemical and temperature resistant sealing systems',
                  'Immediate repair — gaskets cure in hours, not days',
                  'Eliminate inventory of expensive specialty gaskets'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-blue-900 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-xl font-bold mb-4">Belzona Products We Use</h3>
              <div className="space-y-3">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 2111 (D&A Hi-Build Elastomer)</p>
                  <p className="text-xs text-slate-500 mt-1">Conformable gasket and seal creation</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 2131 (D&A Fluid Elastomer)</p>
                  <p className="text-xs text-slate-500 mt-1">Pourable gasket and seal compound</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 1111 (Super Metal)</p>
                  <p className="text-xs text-slate-500 mt-1">Flange face rebuild and repair</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 1221 (Super E-Metal)</p>
                  <p className="text-xs text-slate-500 mt-1">High-temperature flange repair</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Recurring Leak? We Fix the Root Cause.</h2>
          <p className="text-blue-100 mb-8">Contact us for flange repair, gasket fabrication, or permanent leak elimination solutions.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Assessment <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GasketsSeals;
